import { CampaignPublishers } from './store/constants';
import type {
  CampaignDesiredStateDto,
  Country,
  Locale,
} from './store/generated/leela-api-generated';
import { type ExclusionEntry, fromPathnameMatch } from './utils/exclusionEntries';
import { type PathnameMatch, PathnameMatchModes } from './utils/pathnameMatchMode';

export const NO_ACCESS_SUPPORT_EMAIL = 'dynamic-ads@amazon.com';

export const GTI_REGEX =
  /^amzn1\.dv\.gti\.[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/;

export const BENDER_ASPECT_RATIOS = {
  _9X16: '9x16',
  _1X1: '1x1',
};

export enum CreationSource {
  USER = 'USER',
  SYSTEM = 'SYSTEM',
  ADMIN = 'ADMIN',
}

export enum ConfigurationType {
  GTI_BLOCKLIST = 'GTI_BLOCKLIST',
  PUBLISHER_ACCOUNT = 'PUBLISHER_ACCOUNT',
  CONTENT_RULE = 'CONTENT_RULE',
}

export const STRICT_THUMBNAIL_ASPECT_RATIO_PUBLISHERS = [
  CampaignPublishers.META,
];

export const PUBLISHER_THUMBNAIL_REQUIREMENTS: Record<string, boolean> = {
  [CampaignPublishers.ADSP]: false,
  [CampaignPublishers.META]: true,
  [CampaignPublishers.TIKTOK]: true,
  [CampaignPublishers.GOOGLE]: false,
};

export enum BenderAssetType {
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO',
}

export enum BenderTemplateType {
  SINGLE_TITLE = 'SINGLE_TITLE',
  MULTI_TITLE = 'MULTI_TITLE',
}

export const BENDER_SOURCES = {
  VIVID: 'VIVID',
  VAULT: 'VAULT',
};

export const DEFAULT_VIDEO_DURATION = '15';

export const ALLOWED_VIDEO_DURATIONS = new Set([
  '15',
  '18',
  '20',
  '25',
  '28',
  '30',
]);

export const SONATA_PAGE_TYPES = [
  'merch',
  'subscription',
  'channel',
  'tournament',
  'home',
  'tv',
  'movie',
  'genre',
] as const;

export const CAMPAIGN_PAGE_TYPE = 'PVMediaCampaign';

export const DESTINATION_LINK_TYPES = {
  HOME: 'HOME',
  LANDING: 'LANDING',
  DETAIL: 'DETAIL',
} as const;

export const COUNTRY_TO_DOMAIN: Record<string, string> = {
  US: 'https://watch.amazon.com',
  GB: 'https://watch.amazon.co.uk',
  DE: 'https://watch.amazon.de',
  JP: 'https://watch.amazon.co.jp',
};

export const DEFAULT_PRIMEVIDEO_DOMAIN = 'https://app.primevideo.com';

export const DA_GENERATED_PRODUCT_SET_PUBLISHERS: Array<
CampaignDesiredStateDto['publisher']
> = [
  CampaignPublishers.ADSP,
  CampaignPublishers.TIKTOK,
  CampaignPublishers.META,
];

/** Publishers where product set generation is optional (user can choose to provide their own or let backend generate) */
export const OPTIONAL_PRODUCT_SET_PUBLISHERS: Array<
CampaignDesiredStateDto['publisher']
> = [CampaignPublishers.TIKTOK, CampaignPublishers.META];

// Temporarily hard-coded. Will end up fetching from Bender.
export const BENDER_TEMPLATE_OPTIONS = [
  {
    id: '511e32dc-2e3e-4067-832b-26e1a6fb4f92',
    description:
      'Static 1x1 1920x1920 image template for single titles using 16x9 image',
  },
  {
    id: 'a04a4628-18a1-46d4-a79d-f311777db4cc',
    description: 'Static 1x1 1920x1920 image template for single titles',
  },
];

export const ID_TYPES = {
  PAGE_ID: 'pageId',
  STRATEGY_ID: 'strategyId',
  GTI_LIST: 'gtiList',
};

export const GTI_LIST_MIN = 1;
export const GTI_LIST_MAX = 100;
export const GTI_BLOCKLIST_MAX = 500;
export const GTI_CSV_MAX = 500;

// Add one pattern per supported page; use a catch-all pattern when every route supports the assistant.
export const ASSISTANT_ROUTES = ['/campaigns/:campaignId'];

// Paid-media dashboards that offer the data assistant, mapped to the platform label used in its page context.
export const DATA_ASSISTANT_ROUTES: Record<string, string> = {
  '/tiktok-ads-performance': 'TikTok',
  '/meta-ads-performance': 'Meta',
  '/google-ads-performance': 'Google',
  '/adsp-ads-performance': 'ADSP',
};

export const PAGE_IDS = [
  // US
  'uspopularsvodmovies',
  'usnewsvod',
  'usnewamazonoriginals',
  'uspopularsvodtvseries',

  // EU
  'dynamic-ads-es-svod-new-releases',
  'dynamic-ads-it-svod-new-releases',
  'dynamic-ads-fr-svod-new-releases',
  'dynamic-ads-de-svod-new-releases',
  'dynamic-ads-uk-svod-new-releases',
  'dynamic-ads-uk-svod-top-10-tv',
  'dynamic-ads-de-svod-top-10-tv',
  'dynamic-ads-fr-svod-top-10-tv',
  'dynamic-ads-it-svod-top-10-tv',
  'dynamic-ads-es-svod-top-10-tv',
  'dynamic-ads-uk-svod-top-10-movies',
  'dynamic-ads-de-svod-top-10-movies',
  'dynamic-ads-fr-svod-top-10-movies',
  'dynamic-ads-it-svod-top-10-movies',
  'dynamic-ads-es-svod-top-10-movies',
  'dynamic-ads-es-svod-originals-exclusives',
  'dynamic-ads-it-svod-originals-exclusives',
  'dynamic-ads-fr-svod-originals-exclusives',
  'dynamic-ads-uk-svod-originals-exclusives',
  'dynamic-ads-de-svod-originals-exclusives',
  'dynamic-ads-es-svod-young-adults',
  'dynamic-ads-it-svod-young-adults',
  'dynamic-ads-fr-svod-young-adults',
  'dynamic-ads-de-svod-young-adults',
  'dynamic-ads-uk-svod-young-adults',
  'dynamic-ads-uk-svod-christmas',
  'dynamic-ads-de-svod-christmas',
  'dynamic-ads-fr-svod-christmas',
  'dynamic-ads-it-svod-christmas',
  'dynamic-ads-es-svod-christmas',

  // LATCANZ
  'dynamic-ads-latcanz-svod-action-adventure-movies-series',
  'dynamic-ads-latcanz-svod-anime',
  'dynamic-ads-latcanz-svod-comedy-movies-series',
  'dynamic-ads-latcanz-svod-documentaries',
  'dynamic-ads-latcanz-svod-drama-movies-series',
  'dynamic-ads-latcanz-svod-fantasy-movies-series',
  'dynamic-ads-latcanz-svod-horror-movies-series',
  'dynamic-ads-latcanz-svod-kids-family-content',
  'dynamic-ads-latcanz-svod-korean-series',
  'dynamic-ads-latcanz-svod-originals-exclusives',
  'dynamic-ads-latcanz-svod-recently-added-content',
  'dynamic-ads-latcanz-svod-romance-movies-series',
  'dynamic-ads-latcanz-svod-scifi-content',
  'dynamic-ads-latcanz-svod-thriller-suspense-movies-series',
  'dynamic-ads-latcanz-svod-top-ten-content',
  'dynamic-ads-latcanz-svod-unscripted-reality-series',
  'dynamic-ads-latcanz-svod-young-adult-content',

  // Single Titles
  'us-single-title-rop-s2',

  // APAC
  'dynamic-ads-sea-svod-korean-series',
  'dynamic-ads-sea-svod-originals-exclusives',
  'dynamic-ads-sea-svod-recently-added-content',
  'dynamic-ads-sea-svod-top-ten-content',
  'dynamic-ads-in-svod-originals-and-exclusives',
  'dynamic-ads-in-svod-top-movies',
  'dynamic-ads-in-svod-recently-added-movies',
  'dynamic-ads-in-svod-recently-added-tv-series',
  'dynamic-ads-in-svod-latest-movies',
  'dynamic-ads-in-svod-top-imdb',
  'dynamic-ads-in-svod-oscar-nominated',
  'dynamic-ads-in-svod-k-drama',
];

/** Defaults to every Monday at 4PM UTC / 9AM PT */
export const DEFAULT_REFRESH_SCHEDULE_CRON = '0 16 * * 1';

/** Sports business lines default to daily at 4PM UTC / 9AM PT */
export const DEFAULT_SPORTS_REFRESH_SCHEDULE_CRON = '0 16 * * *';

export const BOX_FOLDER_URL_BASE = 'https://app.box.com/folder/';

/** Bare numeric Box folder id, mirroring Bender's `ParseTargetInputPipe`. */
export const BOX_FOLDER_ID_REGEX = /^\d{1,20}$/;

/**
 * EventBridge Scheduler expression Leela uses for every Box folder it enrolls: daily at
 * 13:00 UTC (5 AM PST / 6 AM PDT). Not user-configurable: users are never asked to author
 * or choose a cadence.
 */
export const BOX_SYNC_TARGET_REFRESH_SCHEDULE = 'cron(0 13 * * ? *)';

/**
 * Pathname exclusions pre-filled when a folder is added, so the work-in-progress, superseded, and
 * OS-artifact files creative teams routinely leave in delivery folders are skipped from the first
 * scan unless the user removes them. Kept in plain match modes so each reads as typed text.
 */
export const BOX_DEFAULT_PATHNAME_EXCLUSION_MATCHES: readonly PathnameMatch[] = [
  { mode: PathnameMatchModes.Contains, text: '_wip' },
  { mode: PathnameMatchModes.Contains, text: '/wip' },
  { mode: PathnameMatchModes.Contains, text: '_draft' },
  { mode: PathnameMatchModes.Contains, text: '/draft' },
  { mode: PathnameMatchModes.Contains, text: 'do not use' },
  { mode: PathnameMatchModes.Contains, text: '_dnu' },
  { mode: PathnameMatchModes.Contains, text: '/ideation' },
  { mode: PathnameMatchModes.Contains, text: '/concept' },
  { mode: PathnameMatchModes.Contains, text: '/exploration' },
  { mode: PathnameMatchModes.Contains, text: '/archive' },
  { mode: PathnameMatchModes.Contains, text: '/trash' },
  { mode: PathnameMatchModes.StartsWith, text: '._' },
  { mode: PathnameMatchModes.Contains, text: ' copy.' },
];

export const BOX_DEFAULT_PATHNAME_EXCLUSIONS: readonly ExclusionEntry[] =
  BOX_DEFAULT_PATHNAME_EXCLUSION_MATCHES.map(fromPathnameMatch);

/**
 * Mirrors Bender's sync-target `Locale` enum (PVAssetBenderService `src/localization/locales.ts`),
 * the only values its sync-target DTOs accept as `defaultLocale`. Leela cannot import Bender code,
 * so this is a hand-carried copy: update it whenever that enum changes.
 */
export const SYNC_TARGET_LOCALES = [
  'ar_AE', 'bn_IN', 'cs_CZ', 'da_DK', 'de_DE',
  'en_AE', 'en_AU', 'en_CA', 'en_GB', 'en_IE',
  'en_IN', 'en_NG', 'en_NZ', 'en_SG', 'en_US',
  'en_ZA', 'es_419', 'es_ES', 'es_MX', 'es_US',
  'fr_BE', 'fr_CA', 'fr_FR', 'he_IL', 'hi_IN',
  'it_IT', 'ja_JP', 'kn_IN', 'ko_KR', 'ml_IN',
  'mr_IN', 'nl_BE', 'nl_NL', 'pl_PL', 'pt_BR',
  'pt_PT', 'sv_SE', 'ta_IN', 'te_IN', 'tr_TR',
  'zh_CN', 'zh_TW',
] as const;

export type SyncTargetLocale = (typeof SYNC_TARGET_LOCALES)[number];

/**
 * Box system account a user must share their folder with when Dynamic Ads can't read it.
 *
 * The **email address**, not the `svc-dynamic-ads` alias: Box's collaborator field
 * sometimes resolves the alias but usually requires the address, and a half-working
 * instruction is worse than a verbose one. Rendered verbatim beside a copy button rather
 * than interpolated into a sentence — it is too long and too opaque to retype correctly.
 *
 * Not derivable from Bender, which holds only the Box enterprise id and a Secrets Manager
 * client-credentials pair — so this is a hand-carried copy of the account Bender's Box app
 * authenticates as. If that account is ever rotated, this must be updated with it or the
 * alert sends users to share their folder with a principal that cannot read it.
 */
export const BOX_SYSTEM_ACCOUNT_IDENTITY = 'AutomationUser_2628957_yeE7tlcorP@boxdevedition.com';

export const ASSET_UPDATE_BATCH_SIZE = 100;

/** Special locale for countries with Latin American or Caribbean Spanish */
export const lacSpanishLocale: Locale = 'es_419';

/**
 * An edge case from Bender denoting there are no string translations for a
 *  template and it is therefore supported for all locales.
 */
export const ALL_LOCALES_IDENTIFIER = 'ALL';

/**
 * Canonical list of every Leela locale, used by publishers that support every
 * locale and when Bender returns `ALL_LOCALES_IDENTIFIER`.
 */
export const ALL_SUPPORTED_LOCALES: Locale[] = [
  'ar_AE',
  'bn_IN',
  'cs_CZ',
  'da_DK',
  'de_CH',
  'de_DE',
  'en_AE',
  'en_AU',
  'en_CA',
  'en_GB',
  'en_IE',
  'en_IN',
  'en_NG',
  'en_NZ',
  'en_SG',
  'en_US',
  'en_ZA',
  'es_AR',
  'es_BO',
  'es_CL',
  'es_CO',
  'es_CR',
  'es_DO',
  'es_EC',
  'es_ES',
  'es_GT',
  'es_HN',
  'es_MX',
  'es_NI',
  'es_PA',
  'es_PE',
  'es_PR',
  'es_PY',
  'es_SV',
  'es_US',
  'es_UY',
  'es_VE',
  'fi_FI',
  'fr_BE',
  'fr_CA',
  'fr_CH',
  'fr_FR',
  'he_IL',
  'hu_HU',
  'hi_IN',
  'id_ID',
  'it_CH',
  'it_IT',
  'ja_JP',
  'kn_IN',
  'ko_KR',
  'ml_IN',
  'mr_IN',
  'ms_MY',
  'nl_BE',
  'nl_NL',
  'no_NO',
  'pl_PL',
  'pt_BR',
  'pt_PT',
  'ro_RO',
  'sv_SE',
  'ta_IN',
  'te_IN',
  'th_TH',
  'tr_TR',
  'zh_CN',
  'zh_TW',
  lacSpanishLocale,
];
ALL_SUPPORTED_LOCALES.sort();

/** Countries supporting Latin American or Caribbean Spanish */
export const LAC_SPANISH_COUNTRIES: Country[] = [
  'AR',
  'BO',
  'BR',
  'CL',
  'CO',
  'CR',
  'DO',
  'EC',
  'GT',
  'HN',
  'MX',
  'NI',
  'PA',
  'PE',
  'PR',
  'PY',
  'SV',
  'UY',
  'VE',
];

export interface Region {
  readonly name: string;
  readonly countries: readonly Country[];
}

export const REGIONS: readonly Region[] = [
  { name: 'US', countries: ['US'] },
  { name: 'LATCANZ', countries: ['AU', 'CA', 'NZ', ...LAC_SPANISH_COUNTRIES] },
  { name: 'EMEA', countries: ['AE', 'BE', 'CH', 'CZ', 'DE', 'DK', 'EG', 'ES', 'FI', 'FR', 'GB', 'HU', 'IE', 'IT', 'NL', 'NG', 'NO', 'PL', 'PT', 'RO', 'SA', 'SE', 'TR', 'ZA'] },
  { name: 'APAC', countries: ['ID', 'IN', 'JP', 'MY', 'PH', 'SG', 'TH'] },
];

export const ALL_SUPPORTED_COUNTRIES: Country[] = REGIONS.flatMap(r => [...r.countries]);

/** Resolves a country to its region name. */
export const COUNTRY_TO_REGION: ReadonlyMap<Country, string> = new Map(
  REGIONS.flatMap(region => region.countries.map(country => [country, region.name])),
);
ALL_SUPPORTED_COUNTRIES.sort();

/**
 * Countries are not listed here: campaign forms derive them from the publisher account
 * configs the user can use (see usePublisherConfigCountries).
 */
export interface PublisherConfig {
  readonly supportedLocales: Locale[];
}

const ADSP_CONFIG: PublisherConfig = {
  supportedLocales: [
    // [Language.ENGLISH]: "en",
    'en_AE',
    'en_AU',
    'en_CA',
    'en_GB',
    'en_IE',
    'en_IN',
    'en_NG',
    'en_NZ',
    'en_SG',
    'en_US',
    'en_ZA',
    // [Language.SPANISH]: "es",
    'es_AR',
    'es_BO',
    'es_CL',
    'es_CO',
    'es_CR',
    'es_DO',
    'es_EC',
    'es_ES',
    'es_GT',
    'es_HN',
    'es_MX',
    'es_NI',
    'es_PA',
    'es_PE',
    'es_PR',
    'es_PY',
    'es_SV',
    'es_US',
    'es_UY',
    'es_VE',
    lacSpanishLocale,
    // [Language.GERMAN]: "de",
    'de_DE',
    // [Language.FRENCH]: "fr",
    'fr_BE',
    'fr_CA',
    'fr_FR',
    // [Language.ITALIAN]: "it",
    'it_IT',
    // [Language.JAPANESE]: "ja",
    'ja_JP',
    // [Language.CHINESE]: "zh",
    'zh_CN',
    'zh_TW',
    // [Language.PORTUGUESE]: "pt",
    'pt_BR',
    'pt_PT',
    // [Language.DUTCH]: "nl",
    'nl_BE',
    'nl_NL',
    // [Language.ARABIC]: "ar",
    'ar_AE',
    // [Language.SWEDISH]: "sv",
    'sv_SE',
    // [Language.TURKISH]: "tr",
    'tr_TR',
  ],
};

const META_CONFIG: PublisherConfig = {
  supportedLocales: ALL_SUPPORTED_LOCALES,
};

const TIKTOK_CONFIG: PublisherConfig = {
  supportedLocales: ALL_SUPPORTED_LOCALES,
};

const GOOGLE_CONFIG: PublisherConfig = {
  supportedLocales: ALL_SUPPORTED_LOCALES,
};

/**
 * Each publisher explicitly defines its supported locales
 */
export const PUBLISHER_CONFIGS: Record<string, PublisherConfig> = {
  adsp: ADSP_CONFIG,
  meta: META_CONFIG,
  tiktok: TIKTOK_CONFIG,
  google: GOOGLE_CONFIG,
};

/**
 * Get supported locales for a specific publisher
 * Falls back to all supported locales if publisher is not found
 */
export const getSupportedLocalesForPublisher = (
  publisher: CampaignDesiredStateDto['publisher'],
): Locale[] => {
  return (
    PUBLISHER_CONFIGS[publisher]?.supportedLocales ?? ALL_SUPPORTED_LOCALES
  );
};

export const EMPTY_TABLE_VALUE = '--';

export const FILTERS_STORAGE_KEY = 'tokenized_search_filters';

export const MIDWAY_TOKEN_HEADER = 'Midway-Token';
export const ID_TOKEN_HEADER = 'Id-Token';

// Canonical display order for live-status messages, from furthest-out to live.
// Used both for checkbox rendering and to order the per-message schedule blocks.
export const LIVE_STATUSES = ['COMING_SOON', 'TOMORROW', 'TODAY', 'LIVE_NOW'] as const;

export function liveStatusRank (status: string): number {
  const index = (LIVE_STATUSES as readonly string[]).indexOf(status);
  return index === -1 ? LIVE_STATUSES.length : index;
}

export const FEATURE_FLAGS = {
  ENABLE_REPORTING_DASHBOARD: 'enableReportingDashboard',
  ENABLE_BLOCKLIST: 'enableBlocklist',
  ENABLE_AB_TESTING: 'enableAbTesting',
  ENABLE_SPOTLIGHT_LAL: 'enableSpotlightLal',
  ENABLE_SOCIAL_SIGNALS: 'enableSocialSignals',
  ENABLE_GOOGLE_DEMANDGEN: 'enableGoogleDemandGen',
  ENABLE_EXECUTION_LAYER: 'enableExecutionLayer',
  ENABLE_SPORTS_BUSINESS_LINES: 'enableSportsBusinessLines',
  ENABLE_PAID_MEDIA_DASHBOARD: 'enablePaidMediaDashboard',
  ENABLE_ASSET_EVAL_TRIGGER: 'enableAssetEvalTrigger',
  ENABLE_ENTITY_METRICS: 'enableEntityMetrics',
  ENABLE_BOX_FOLDERS: 'enableBoxFolders',
  ENABLE_ASSET_IMPROVEMENTS: 'enableAssetImprovements',
  PERMISSIONS_ENFORCEMENT_ENABLED: 'permissionsEnforcementEnabled',
  ENABLE_DATA_AGENT: 'enableDataAgent',
  ENABLE_ISSUE_REPORTS: 'enableIssueReports',
} as const;

export const NOTIFICATION_RECIPIENTS_NO_BUSINESS_UNIT_CODE =
  'CAMPAIGN_BUSINESS_UNIT_MISSING';

export type BulkEditMode = 'notificationRecipients' | 'blocklist' | 'status' | 'schedule' | 'unarchive';

/** Color mappings for social signals trend topic pills */
export const TOPIC_COLORS: Record<string, { bg: string; text: string }> = {
  entertainment: { bg: '#F3E8FF', text: '#7C3AED' },
  sports: { bg: '#ECFDF5', text: '#059669' },
  technology: { bg: '#E0E7FF', text: '#3730A3' },
  health: { bg: '#FEF2F2', text: '#DC2626' },
  business: { bg: '#FEFCE8', text: '#A16207' },
  politics: { bg: '#FFF7ED', text: '#EA580C' },
  lifestyle: { bg: '#FDF2F8', text: '#DB2777' },
};

/** Brand color mappings for publishers/platforms used across Leela */
export const PUBLISHER_COLORS: Record<string, { bg: string; text: string; accent: string }> = {
  meta: { bg: '#F0F4FF', text: '#4A5568', accent: '#4267B2' },
  tiktok: { bg: '#F5F5F5', text: '#4A5568', accent: '#7C3AED' },
  google: { bg: '#F5F7F5', text: '#4A5568', accent: '#34A853' },
  serpapi_google_trends: { bg: '#F5F7F5', text: '#4A5568', accent: '#34A853' },
  adsp: { bg: '#FFF8F0', text: '#4A5568', accent: '#FF9900' },
};

/** @deprecated Use PUBLISHER_COLORS instead */
export const PLATFORM_COLORS = PUBLISHER_COLORS;

/** Chart accent colors for publisher donuts/lines */
export const PUBLISHER_CHART_COLORS: Record<string, string> = {
  meta: '#4267B2',
  tiktok: '#7C3AED',
  google: '#34A853',
  adsp: '#FF9900',
};

/** Chart colors for paid-media insight series */
export const PAID_MEDIA_CHART_COLORS: Record<string, string> = {
  clicks: '#7C3AED',
  spend: '#4267B2',
};

/** Chart colors for campaign status */
export const CAMPAIGN_STATUS_COLORS: Record<string, string> = {
  ACTIVE: '#34A853',
  PAUSED: '#FF9900',
  SUSPENDED: '#DC3545',
  ARCHIVED: '#6B7280',
};

/** Chart colors for workflow execution status */
export const WORKFLOW_STATUS_COLORS: Record<string, string> = {
  COMPLETED: '#34A853',
  FAILED: '#DC3545',
  IN_PROGRESS: '#4267B2',
  QUEUED: '#FF9900',
};

/** Chart colors for asset approval status */
export const ASSET_STATUS_COLORS: Record<string, string> = {
  APPROVED: '#34A853',
  PENDING: '#FF9900',
  REJECTED: '#DC3545',
  PROCESSING: '#4267B2',
};

/** Chart colors for business lines */
export const BUSINESS_LINE_COLORS: Record<string, string> = {
  SVOD: '#4267B2',
  TVOD: '#7C3AED',
  CHANNEL: '#34A853',
  CHANNELS: '#34A853',
  SPORTS_SVOD: '#E67E22',
  SPORTS_CHANNEL: '#D35400',
  SPORTS_TVOD: '#A04000',
  SPORTS: '#FF9900',
};

/** Chart colors for workflow types */
export const WORKFLOW_TYPE_COLORS: Record<string, string> = {
  CAMPAIGN_CREATE: '#4267B2',
  CAMPAIGN_UPDATE: '#34A853',
  CAMPAIGN_DELETE: '#DC3545',
  CAMPAIGN_RESTORE: '#6B7280',
  PRODUCT_UPDATE: '#7C3AED',
  PRODUCT_RESTORE: '#FF9900',
  ASSET_REMOVE: '#DC3545',
};
